/**
 * Relative time, computed — and the vocabulary it is allowed to say.
 *
 * ── THE BUG THIS EXISTS FOR ──────────────────────────────────────────────────
 * Every timestamp a customer saw was a frozen string. `এখন` was drawn on a step that
 * was current when the frame was drawn, and it stayed `এখন` for an order placed three
 * weeks ago, because the string was transcribed rather than derived. A screen that says
 * "now" about something that happened in August is not a rendering nit: it is the
 * product telling the customer something false about their own order.
 *
 * ── WHY IT DOES NOT TICK, AND THAT IS DELIBERATE ─────────────────────────────
 * There is no interval, no `requestAnimationFrame`, no subscription. The phrase is
 * computed ONCE per render, from the `now` the caller passes in. A live-updating
 * countdown repaints different glyphs every second, and the fidelity gate
 * (`tools/visual-regression/`, frozen at 2%) screenshots a running build: a screen whose
 * text changes between the design capture and the build capture scores a diff that is
 * about the clock rather than about the screen. `docs/bangla-needed/auth.md § 8` already
 * records the same conclusion for A4's resend countdown and calls wiring it "a product
 * decision". So: correct when painted, and honest about not moving. A screen that needs
 * a moving clock can re-render itself and will get a new phrase; nothing here will do it
 * behind its back.
 *
 * `now` is a REQUIRED argument rather than a `Date.now()` default, for the same reason:
 * a function that reads the clock itself cannot be tested for the boundary between
 * `গতকাল` and `২ দিন আগে`, and a screen that reads the clock itself cannot be captured
 * twice and compared.
 *
 * ── EVERY PHRASE BELOW IS TRANSCRIBED. NONE IS WRITTEN ───────────────────────
 * CLAUDE.md and docs/01 § prohibited: Bangla is never invented. The phrases this module
 * may produce were counted in `design/*.dc.html` before it was written:
 *
 *     এখন           40 occurrences      গতকাল          6
 *     আজ            83                  গত মাস         4   (B1, `DPDC · গত মাস`)
 *     ২ দিন আগে / ৩০ দিন আগে            ২ ঘণ্টা আগে
 *
 * Those six shapes are the whole vocabulary. Anything outside them — minutes, weeks,
 * "N months ago", anything with a year in it — has NO drawn phrase, so this module
 * returns `null` for it and the caller renders what the server sent instead. Filling the
 * gap with a plausible sentence is the failure mode this rule exists to prevent:
 * `docs/bangla-needed/account-activity.md § timestamps` already records that "there is
 * no Bangla relative-time rule set in this repository", and one invented here would
 * become indistinguishable from drawn copy to everybody who read it afterwards. The
 * missing shapes are listed in `docs/bangla-needed/home-search.md § 3`.
 *
 * Numerals come from `@oprappo/tokens`' `toBanglaDigits`, which is the repository's one
 * digit map (docs/02 § numerals). This module does not carry a second one.
 */
import { toBanglaDigits } from '@oprappo/tokens';

/**
 * What a timestamp IS relative to a moment — before any language is chosen.
 *
 * The shape is separated from the phrase on purpose. Deciding "this is four days ago"
 * is arithmetic and is the same in every language; deciding that four days ago reads
 * `৪ দিন আগে` is copy, and copy is drawn rather than computed. Keeping them apart is
 * what lets the arithmetic be tested exhaustively while the vocabulary stays a closed
 * list of transcriptions.
 */
export type RelativeShape =
  | { kind: 'now' }
  | { kind: 'hours_ago'; hours: number }
  | { kind: 'today' }
  | { kind: 'yesterday' }
  | { kind: 'days_ago'; days: number }
  | { kind: 'last_month' }
  /** Older than the drawn vocabulary reaches. Carries the date so a caller can format it. */
  | { kind: 'older'; iso: string }
  /** The input was not a timestamp at all. Never rendered; never guessed at. */
  | { kind: 'unknown' };

/**
 * `এখন` is drawn on a step that is happening. Under a minute is the widest reading of
 * that which is still true, and it is the narrowest thing the word can mean.
 */
export const NOW_WINDOW_MS = 60_000;

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** Midnight of the calendar day `t` falls in, in the viewer's own zone. */
function startOfLocalDay(t: Date): number {
  return new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
}

/**
 * Which shape `iso` has, seen from `now`.
 *
 * CALENDAR DAYS, NOT 24-HOUR BLOCKS. `গতকাল` means the day before this one, not "between
 * 24 and 48 hours ago": at 00:30 an event from 23:00 is yesterday to every reader, and a
 * duration-based rule would call it `এখন`-adjacent and say `আজ`. The day boundary is the
 * viewer's local midnight, because the customer reads the screen in their own day.
 *
 * A timestamp in the FUTURE is `now`, not a negative count. Clock skew between a phone
 * and a Worker is routine and small, and the alternatives are a phrase the design does
 * not draw or an arithmetic sign on a user's screen.
 */
export function relativeShape(iso: string, now: Date): RelativeShape {
  const t = new Date(iso);
  const ms = t.getTime();
  if (Number.isNaN(ms)) return { kind: 'unknown' };

  const delta = now.getTime() - ms;
  if (delta < NOW_WINDOW_MS) return { kind: 'now' };

  const dayDelta = Math.round((startOfLocalDay(now) - startOfLocalDay(t)) / DAY_MS);

  if (dayDelta <= 0) {
    // The same calendar day. Under six hours the drawn `N ঘণ্টা আগে` is the more useful
    // of the two true phrases; beyond that `আজ` is what E1 draws on a row from today.
    const hours = Math.floor(delta / HOUR_MS);
    return hours >= 1 && hours < 6 ? { kind: 'hours_ago', hours } : { kind: 'today' };
  }
  if (dayDelta === 1) return { kind: 'yesterday' };
  // `৩০ দিন আগে` is drawn, so the day count reaches 30 rather than stopping at a week.
  if (dayDelta <= 30) return { kind: 'days_ago', days: dayDelta };
  // `গত মাস` is drawn on B1's repeat card. It is a calendar claim, so it is made only
  // about the calendar month before this one — not about "31 to 60 days".
  if (isPreviousCalendarMonth(t, now)) return { kind: 'last_month' };
  return { kind: 'older', iso };
}

function isPreviousCalendarMonth(t: Date, now: Date): boolean {
  const months = (now.getFullYear() - t.getFullYear()) * 12 + (now.getMonth() - t.getMonth());
  return months === 1;
}

/**
 * The phrase, or `null` when the design draws none.
 *
 * `null` is the whole point of this function's return type. Five shapes have a drawn
 * phrase and three do not, and a caller that receives `null` renders the string the
 * server already sent rather than a sentence this file made up.
 */
export function relativeTimeBn(iso: string, now: Date): string | null {
  const shape = relativeShape(iso, now);
  switch (shape.kind) {
    case 'now':
      return 'এখন';
    case 'hours_ago':
      // `২ ঘণ্টা আগে` — `Oprappo P2 Projects and Explore.dc.html`. The numeral is the
      // only part that varies, and it varies through the repository's one digit map.
      return `${toBanglaDigits(shape.hours)} ঘণ্টা আগে`;
    case 'today':
      return 'আজ';
    case 'yesterday':
      return 'গতকাল';
    case 'days_ago':
      // `২ দিন আগে`, `৩০ দিন আগে` — both drawn, which is what makes the count a variable
      // in a drawn phrase rather than a phrase assembled out of two drawn words.
      return `${toBanglaDigits(shape.days)} দিন আগে`;
    case 'last_month':
      // `গত মাস` — `Oprappo P0 Screens.dc.html`, frame B1, `DPDC · গত মাস`.
      return 'গত মাস';
    case 'older':
    case 'unknown':
      // No drawn phrase reaches here. See this file's header and
      // `docs/bangla-needed/home-search.md § 3`; the caller renders the server's own
      // string, which is already localised (docs/05 § conventions).
      return null;
  }
}
